import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Button, Input } from '@follow/ui';

import AssetsService from '../../services/AssetsService';
import ModalConfirmDelete from '../../components/Modals/ModalConfirmDelete';
import useLocationRole from '../../hooks/useLocationRole';

const SiteSettings = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Renaming and deleting a site are owner-only; a teammate reads it.
  const { canManage } = useLocationRole(siteId);

  const { data: location } = useQuery({
    queryKey: ['location', siteId],
    queryFn: () => AssetsService.getLocation(Number(siteId)),
    enabled: !!siteId,
    select: (data) => data.location,
  });

  const [name, setName] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (location && !isDirty) {
      setName(location.name);
    }
  }, [location, isDirty]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setIsDirty(true);
  };

  const handleCancel = () => {
    if (location) {
      setName(location.name);
      setIsDirty(false);
    }
  };

  const updateLocationMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<{ name: string }> }) =>
      AssetsService.updateLocation(id, data),
    onSuccess: () => {
      toast.success('Site mis à jour');
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey: ['location', siteId] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
    onError: () => {
      toast.error('Erreur lors de la mise à jour');
    },
  });

  const deactivateLocationMutation = useMutation({
    mutationFn: (id: number) => AssetsService.deactivateLocation(id),
    onSuccess: () => {
      toast.success('Site supprimé');
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      navigate('/sites');
    },
    onError: () => {
      toast.error('Erreur lors de la suppression');
    },
    onSettled: () => {
      setShowDeleteModal(false);
    },
  });

  const handleSave = () => {
    if (!siteId) return;
    updateLocationMutation.mutate({ id: Number(siteId), data: { name } });
  };

  const handleDelete = () => {
    if (!siteId) return;
    deactivateLocationMutation.mutate(Number(siteId));
  };

  return (
    <>
      <div className="w-full max-w-[640px] px-4 flex flex-col justify-start items-start gap-8">
        <div className="self-stretch inline-flex justify-start items-start gap-4">
          <div className="flex-1 inline-flex flex-col justify-center items-start">
            <h1 className="text-neutral typo-h1">Paramètres du site</h1>
          </div>
        </div>
      </div>
      <div className="w-full max-w-[640px] px-4 flex flex-col justify-start items-start gap-4">
        <h2 className="text-neutral typo-h2">Détails</h2>
        <div className="self-stretch p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col justify-center items-start gap-6 overflow-hidden">
          <div className="w-full max-w-[480px] flex flex-col justify-start items-start gap-1">
            <label className="self-stretch text-neutral-80 typo-label">
              Nom du site
            </label>
            <Input
              tone="dark"
              type="text"
              value={name}
              onChange={handleNameChange}
              disabled={!canManage}
            />
          </div>
          {/* TODO: re-add when design is ready */}
          {/* description, address, banner, websiteUrl, termsUrl, schedules */}
          {canManage && (
            <div className="self-stretch inline-flex justify-end items-start gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Annuler
              </Button>
              <Button variant="primary" tone="light" size="sm" onClick={handleSave} disabled={!isDirty}>
                Sauvegarder
              </Button>
            </div>
          )}
        </div>
      </div>
      {canManage && (
      <div className="w-full max-w-[640px] px-4 flex flex-col justify-start items-start gap-4">
        <h2 className="text-neutral typo-h2">Danger zone</h2>
        <div className="self-stretch p-4 bg-error-100 rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-error-500 flex flex-col justify-center items-start gap-6 overflow-hidden">
          <div className="self-stretch flex flex-col justify-start items-start">
            <span className="self-stretch text-neutral typo-label">
              Supprimer ce site supprimera aussi ses stations
            </span>
            <span className="self-stretch text-neutral-80 typo-small">
              Cette action est irréversible. Si ce site possède des stations qui sont déployées, veuillez nous contacter avant d'effectuer cette opération afin qu'on vous accompagne pour désactiver ou déplacer vos stations.
            </span>
          </div>
          <Button variant="destructive" size="sm" onClick={() => setShowDeleteModal(true)}>
            Supprimer ce site
          </Button>
        </div>
      </div>
      )}
      <ModalConfirmDelete
        visible={showDeleteModal}
        title="Supprimer ce site"
        description="Cette action est irréversible. Si ce site possède des stations qui sont déployées, veuillez nous contacter avant d'effectuer cette opération afin qu'on vous accompagne pour désactiver ou déplacer vos stations."
        confirmLabel="Supprimer ce site"
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default SiteSettings;
